import { createContext, useCallback, useEffect, useState } from 'react'
import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth'
import { useBoolean } from 'hooks'

const initialValue = {
    user: null,
}

type AuthType = {
    user: null | { uid: string; isAnonymous: boolean }
}

export const AuthContext = createContext<
    AuthType & {
        ssoLoginPage: string
        setSsoLoginPage: (_val: string) => void
        setLoginPage: (_val: boolean) => void
        showLoginPage: boolean
        isLoading: boolean
    }
>({
    user: null,
    setLoginPage: () => {},
    isLoading: false,
    showLoginPage: false,
    ssoLoginPage: '',
    setSsoLoginPage: () => {},
})

type Props = {
    children: JSX.Element
}

export const AuthProvider = ({ children }: Props) => {
    const [auth, setAuth] = useState<AuthType>(initialValue)

    const loading = useBoolean(true)
    const showLoginPage = useBoolean()
    const [ssoLoginPage, setSsoLoginPage] = useState('')
    const cancelLoading = loading.setFalse

    const hideSsoLoginPage = useCallback(() => {
        setSsoLoginPage('')
    }, [setSsoLoginPage])
    const hideGeneralLoginPage = showLoginPage.setFalse
    const hideLoginPage = useCallback(() => {
        hideSsoLoginPage()
        hideGeneralLoginPage()
    }, [hideSsoLoginPage, hideGeneralLoginPage])

    useEffect(() => {
        onAuthStateChanged(getAuth(), user => {
            if (!user) {
                signInAnonymously(getAuth())
                    .catch(({ code, message }) => {
                        if (code === 'auth/operation-not-allowed') {
                            console.error('You must enable Anonymous auth in the Firebase Console.')
                        } else {
                            console.error(message, 'signInAnonymously')
                        }
                    })
                    .finally(cancelLoading)

                hideLoginPage()
                return
            }

            user.getIdTokenResult()
                .then(auth => {
                    setAuth({
                        user: {
                            uid: String(auth.claims.user_id),
                            isAnonymous: auth.signInProvider === 'anonymous',
                        },
                    })
                })
                .finally(cancelLoading)

            hideLoginPage()
        })
    }, [hideLoginPage, cancelLoading])

    return (
        <AuthContext.Provider
            value={{
                ...auth,
                isLoading: loading.isTrue,
                showLoginPage: showLoginPage.isTrue,
                setLoginPage: showLoginPage.set,
                ssoLoginPage,
                setSsoLoginPage,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}
